import React, { createContext, useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { scheduleApi } from "@shared/apis"
import { User } from "@shared/apis/account"
import { useWatchingViewer } from "@shared/hooks"
import { isDev, isLocal, kick } from "@shared/modules"

import { useAccount } from "./account"

interface ContextProps {
  schedule: Schedule | null
  setSchedule: React.Dispatch<React.SetStateAction<Schedule | null>>
}

const scheduleContext = createContext<ContextProps>({
  schedule: null,
  setSchedule: () => {},
})

interface Props {
  children: React.ReactNode
  isDetect?: boolean
}

function ScheduleProvider({ children, isDetect = true }: Props) {
  const { scheduleId } = useParams<{ scheduleId?: string }>()
  const { account, setAccount } = useAccount()
  const [schedule, setSchedule] = useState<Schedule | null>(null)

  const role = account?.viewer?.role

  useWatchingViewer({ schedule, account, setAccount, isDetect })

  useEffect(() => {
    if (!scheduleId || !role) return

    const getCurrentSchedule = () => {
      scheduleApi
        .findBy(scheduleId!)
        .then(({ data: schedule }) => {
          if (
            role === "ANONYMOUS" &&
            !schedule.isGuestVodPlay &&
            window.location.pathname.includes("vods")
          ) {
            kick("접근 권한이 없습니다.")
            return
          }
          if (isLocal || isDev) {
            setSchedule({ ...schedule, isChatEnabled: true })
          } else {
            setSchedule(schedule)
          }
        })
        .catch(({ response }) => {
          const { data, status } = response
          let { message } = data

          if (status === 404) {
            message = "존재 하지 않는 스케줄 입니다"
          }

          kick(message)
        })
    }

    getCurrentSchedule()
  }, [scheduleId, role])

  const contextValue = {
    schedule,
    setSchedule,
  }

  return (
    <scheduleContext.Provider value={contextValue}>
      {children}
    </scheduleContext.Provider>
  )
}

function useSchedule() {
  return useContext(scheduleContext)
}

export { ScheduleProvider, scheduleContext, useSchedule }

export interface Schedule {
  id: number
  title: string
  key: string
  scheduleStatus: ScheduleStatus
  streamingStatus: StreamingStatus
  streamingStart: boolean
  streamingEnd: boolean
  user: User
  streamingUrl: string
  mobileStreamingUrl: string
  paymentInformation: PaymentInformation
  scheduleEnd: boolean
  createdAt: string
  streamingUsername: string
  streamKey: string
  applicationId: string
  streamHost: string
  isActivateBlockChat?: boolean
  progressStatus?: ProgressStatus | null
  isEmoticon: boolean
  isCongratsMessage: boolean
  isGuestVodPlay: boolean
  isChatEnabled?: boolean
}

type ProgressStatus =
  | "WAITING"
  | "PROGRESSING"
  | "COMPLETED"
  | "CANCELED"
  | "FAILED"

type ScheduleStatus = "WAITING" | "START" | "END"

export type StreamingStatus = "START" | "WAITING" | "END"
export type PaymentInformation = "ALL" | "FREE" | "PAID"
